export const JOINT_CREDITOR_NOTICE = 'NOTICE TO ALL JOINT CREDITORS for the debtor and spouse – to be paid as a joint debt under this provision, your proof of claim, as filed, must identify and demonstrate that the debt is a JOINT obligation held by debtor and spouse, as Debtor(s) holds property in tenancy by the entirety, any net equity will be shared pro-rata by joint creditors.';

export function normalizePlanLanguageAmount(value, label = 'Amount') {
  if (!['string', 'number'].includes(typeof value)) throw new Error(`${label} is required.`);
  const text = String(value).trim().replace(/^\$\s*/, '');
  if (!text) throw new Error(`${label} is required.`);
  if (!/^(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d{1,2})?$/.test(text)) {
    throw new Error(`${label} must be a non-negative dollar amount with no more than two decimal places.`);
  }
  const amount = Number(text.replace(/,/g, ''));
  if (!Number.isFinite(amount) || !Number.isSafeInteger(Math.round(amount * 100))) {
    throw new Error(`${label} is too large.`);
  }
  return amount.toFixed(2);
}

const displayAmount = amount => Number(amount).toLocaleString('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function buildTaxRefundCreditLanguage(retentionAmount) {
  const amount = normalizePlanLanguageAmount(retentionAmount, 'Retained refund amount');
  return `Each year, the Debtor may retain $${displayAmount(amount)} from tax refund, plus the full amount of any Child Tax Credit (CTC) and Earned Income Credit (EIC) reflected on the applicable tax return. The Debtor shall turn over any remaining balance of refund to the Trustee for the benefit of creditors`;
}

export function buildBestInterestLanguage(kind, { jointAmount, nonJointAmount } = {}) {
  if (!['non_joint', 'joint_only', 'joint_and_non_joint'].includes(kind)) {
    throw new Error('Select a best-interest template.');
  }
  const joint = kind !== 'non_joint' ? normalizePlanLanguageAmount(jointAmount, 'Joint best-interest amount') : null;
  const nonJoint = kind !== 'joint_only' ? normalizePlanLanguageAmount(nonJointAmount, 'Non-joint best-interest amount') : null;
  if (kind === 'non_joint') {
    return {
      description: `Unsecured creditors will be paid pro-rata on a funds available basis. To meet the best interest requirements of 11 U.S.C. § 1325(a)(4), unless the claims are paid in full, the total amount to be paid to general unsecured creditors must be at least $${displayAmount(nonJoint)} less any amount paid to priority unsecured creditors.`,
      otherAmount: nonJoint,
      nonstandardProvision: '',
    };
  }
  const paragraphs = [
    `a. In order to meet the Best Interest requirements of 11 U.S.C. §1325(a)(4) as it relates to the jointly owned marital home held by the Tenants by the entirety, unless the total joint claims are paid in full, the total amount to be paid to joint general unsecured creditors must be at least $${displayAmount(joint)} less any amount paid to priority unsecured creditors.`,
    'NOTICE to all JOINT creditors - review the special provisions in Section 8.1.',
    kind === 'joint_only'
      ? 'b. All non-joint creditors shall be paid a pro rata distribution.'
      : `b. Unless the non-joint general unsecured claims are paid in full, the total amount to be paid to non-joint general unsecured creditors must be at least $${displayAmount(nonJoint)} less any amount paid to priority unsecured creditors.`,
  ];
  const totalCents = Math.round(Number(joint) * 100) + Math.round(Number(nonJoint || 0) * 100);
  if (!Number.isSafeInteger(totalCents)) throw new Error('The combined best-interest amount is too large.');
  return {
    description: paragraphs.join('\n'),
    otherAmount: (totalCents / 100).toFixed(2),
    nonstandardProvision: JOINT_CREDITOR_NOTICE,
  };
}

export function buildPdiLanguage(amount) {
  const value = normalizePlanLanguageAmount(amount, 'PDI amount');
  return `To meet the Projected Disposable Income requirements of 11 U.S.C. § 1325(b)(1)(B), the total amount paid to unsecured creditors must be at least $${displayAmount(value)} unless their claims are paid in full.`;
}

const normalizeProvision = text => String(text || '').normalize('NFKC')
  .replace(/[\u2013\u2014]/g, '-')
  .replace(/\s+/g, ' ').trim().toLowerCase();

export function hasNonstandardProvision(provisions, description) {
  const needle = normalizeProvision(description);
  if (!needle) return false;
  const items = Array.isArray(provisions) ? provisions : [provisions];
  return items.some(item => normalizeProvision(typeof item === 'string' ? item : item?.description).includes(needle));
}

export function appendNonstandardProvisionText(existingText, description) {
  const existing = String(existingText || '');
  if (!description || hasNonstandardProvision(existing, description)) return existing;
  return existing ? `${existing}\n\n${description}` : description;
}
